// src/widget/controller.ts
import type { ResolvedWidgetConfig } from "./config";
import {
  initialState,
  type WidgetState,
  type ChatMessage,
  type SchedulingProvider,
  type SchedulingSlot,
  WidgetError,
} from "./state";
import { ChatGrowAPI } from "../services/api";
import { SSEStreamClient } from "../services/stream";
import { normalizeDomain, isDomainAllowed, getWidgetHost } from "../utils/auth";
import {
  getOrCreateVisitorId,
  getActiveConversationId,
  setActiveConversationId,
} from "../services/storage";
import { loadDraft, saveDraft, clearDraft } from "../services/drafts";

function now(): number {
  return Date.now();
}

function localId(prefix: string): string {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

type Listener = (s: WidgetState) => void;

export class WidgetController {
  private lastUserMessage?: string;
  private lastUserMessageId?: string;

  private streamBuffer = "";
  private streamFlushTimer?: number | undefined;

  /** Guards async stream callbacks */
  private streamSession = 0;

  private listeners = new Set<Listener>();

  readonly api: ChatGrowAPI;
  readonly stream: SSEStreamClient;
  readonly visitorId: string;

  private state: WidgetState;

  constructor(
    private readonly config: ResolvedWidgetConfig,
    private readonly hostElement: HTMLElement,
  ) {
    this.api = new ChatGrowAPI(config.apiBase);
    this.stream = new SSEStreamClient(config.apiBase);

    this.visitorId = getOrCreateVisitorId(config.agentId);

    const restored = config.defaultOpen
      ? undefined
      : (getActiveConversationId(config.agentId, this.visitorId) ?? undefined);

    this.state = initialState(this.visitorId, restored);
    this.state.open = config.defaultOpen;
  }

  get snapshot(): WidgetState {
    return this.state;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private setState(patch: Partial<WidgetState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }

  /* =====================
     Init
  ====================== */

  async init(): Promise<void> {
    this.setState({ loading: true, error: undefined });

    try {
      const agent = await this.api.getAgentPublic(this.config.agentId);

      // 🚫 HARD BLOCK: disabled widget or domain mismatch
      if (!this.validateWebsiteAccess(agent)) {
        this.setState({
          loading: false,
          error: {
            kind: "init",
            title: "Widget unavailable",
            description:
              "This chat widget is not enabled for this website or domain.",
            recoverable: false,
          },
        });

        // Optional: completely hide widget DOM if you want zero UI footprint
        // this.hostElement.style.display = 'none';

        return;
      }

      this.applyBranding(agent);

      // Ensure properties are at least empty arrays if not provided
      if (!agent.initialMessages) agent.initialMessages = [];
      if (!agent.suggestedMessages) agent.suggestedMessages = [];

      // Override with config if provided
      if (this.config.initialMessages.length > 0) {
        agent.initialMessages = this.config.initialMessages;
      }
      if (this.config.suggestions.length > 0) {
        agent.suggestedMessages = this.config.suggestions;
      }

      this.setState({ agent });

      let conversationId = this.state.conversationId;
      let conversationStatus: WidgetState["conversationStatus"] = "OPEN";

      let conversations = this.state.conversations;
      if (this.config.showRecentChats) {
        conversations = await this.api.listConversations(
          this.config.agentId,
          this.visitorId,
        );
      }

      let messages: ChatMessage[] = [];
      let escalated = false;

      if (conversationId) {
        const convo = await this.api.loadConversation(conversationId);
        messages = convo.messages ?? [];
        conversationStatus = (convo.status as any) || "OPEN";
        escalated = conversationStatus === "ESCALATED";
      }

      const draft = loadDraft(
        this.config.agentId,
        this.visitorId,
        conversationId,
      );

      let showSuggestions = false;
      let showInitialMessage =
        !!agent.initialMessages?.length &&
        !this.state.open &&
        messages.length === 0 &&
        !draft;

      // Handle default open population
      if (
        this.state.open &&
        messages.length === 0 &&
        !draft &&
        agent.initialMessages?.length
      ) {
        const newMessages: ChatMessage[] = agent.initialMessages.map(
          (msg: string) => ({
            id: localId("init"),
            role: "AGENT",
            content: msg,
            createdAt: now(),
            status: "sent",
          }),
        );
        messages = [...messages, ...newMessages];
        showSuggestions = true;
      }

      this.setState({
        conversationId,
        conversationStatus,
        conversations,
        messages,
        input: draft,
        loading: false,
        showInitialMessage,
        showSuggestions: showSuggestions,
        escalated,
      });
    } catch {
      this.setState({
        loading: false,
        error: this.makeError("init"),
      });
    }
  }

  /* =====================
     UI Controls
  ====================== */

  open(): void {
    if (this.state.open) return;
    this.handleOpen();
  }

  close(): void {
    this.setState({ open: false });
  }

  toggle(): void {
    if (this.state.open) {
      this.close();
    } else {
      this.handleOpen();
    }
  }

  private handleOpen(): void {
    let updates: Partial<WidgetState> = { open: true };

    if (
      this.state.showInitialMessage &&
      this.state.agent?.initialMessages?.length
    ) {
      const newMessages: ChatMessage[] = this.state.agent.initialMessages.map(
        (msg: string) => ({
          id: localId("init"),
          role: "AGENT",
          content: msg,
          createdAt: now(),
          status: "sent",
        }),
      );

      updates = {
        ...updates,
        showInitialMessage: false,
        showSuggestions: true,
        messages: [...this.state.messages, ...newMessages],
      };
    }

    this.setState(updates);
  }

  setScreen(screen: WidgetState["screen"]): void {
    this.setState({ screen });
  }

  setOnline(online: boolean): void {
    this.setState({ online });
  }

  clearError() {
    this.setState({ error: undefined });
  }

  dismissInitialMessage(): void {
    this.setState({ showInitialMessage: false });
  }

  /* =====================
     Conversations
  ====================== */

  async escalateConversation(): Promise<void> {
    const convoId = this.state.conversationId;
    if (!convoId) return;
    try {
      await this.api.escalateConversation(convoId);
      this.setState({ escalated: true });
    } catch (e) {
      console.error("Failed to escalate", e);
    }
  }

  async updateVisitorDetails(name: string, email: string): Promise<void> {
    try {
      await this.api.updateVisitor(
        this.config.agentId,
        this.visitorId,
        name,
        email,
      );
      if (this.state.screen === "details") {
        this.setScreen("chat");
      }
    } catch (e) {
      console.error("Failed to update visitor", e);
    }
  }

  async startNewChat(): Promise<void> {
    this.stream.stop();
    this.streamSession++;

    setActiveConversationId(
      this.config.agentId,
      this.visitorId,
      undefined as any,
    );

    const draft = loadDraft(this.config.agentId, this.visitorId, undefined);

    let updates: Partial<WidgetState> = {
      conversationId: undefined,
      conversationStatus: "OPEN",
      messages: [],
      input: draft,
      screen: "chat",
      error: undefined,
      showInitialMessage: !!this.state.agent?.initialMessages?.length,
      showSuggestions: false, // Wait for initial messages or user input
      streaming: undefined,
      sending: false,
      streamStatus: "idle",
      escalated: false,
    };

    if (
      this.state.open &&
      updates.showInitialMessage &&
      this.state.agent?.initialMessages?.length
    ) {
      const newMessages: ChatMessage[] = this.state.agent.initialMessages.map(
        (msg: string) => ({
          id: localId("init"),
          role: "AGENT",
          content: msg,
          createdAt: now(),
          status: "sent",
        }),
      );

      updates = {
        ...updates,
        showInitialMessage: false,
        showSuggestions: true,
        messages: newMessages,
      };
    }

    this.setState(updates);
  }

  async switchConversation(conversationId: string): Promise<void> {
    this.stream.stop();
    this.streamSession++;

    setActiveConversationId(
      this.config.agentId,
      this.visitorId,
      conversationId,
    );

    this.setState({ loading: true, error: undefined });

    try {
      const convo = await this.api.loadConversation(conversationId);
      const draft = loadDraft(
        this.config.agentId,
        this.visitorId,
        conversationId,
      );

      const status = (convo.status as any) || "OPEN";

      this.setState({
        conversationId,
        conversationStatus: status,
        messages: convo.messages ?? [],
        input: draft,
        screen: "chat",
        loading: false,
        showSuggestions: (convo.messages ?? []).length === 0,
        escalated: status === "ESCALATED",
      });
    } catch {
      this.setState({
        loading: false,
        error: this.makeError("conversation"),
      });
    }
  }

  /* =====================
     Input
  ====================== */

  setInput(value: string): void {
    saveDraft(
      this.config.agentId,
      this.visitorId,
      this.state.conversationId,
      value,
    );

    this.setState({ input: value });
  }

  clearInput(): void {
    this.setState({ input: "" });
  }

  submitInput(): void {
    const text = this.snapshot.input;
    void this.send(text);
  }

  retryError(): void {
    const err = this.state.error;
    if (!err) return;

    this.clearError();

    switch (err.kind) {
      case "init":
        void this.init();
        return;

      case "conversation": {
        const convoId = this.state.conversationId;
        if (convoId) {
          void this.switchConversation(convoId);
        } else {
          void this.init();
        }
        return;
      }

      case "offline":
      case "stream": {
        if (!navigator.onLine) return;
        if (this.lastUserMessage) {
          void this.send(this.lastUserMessage, { retry: true });
        }
        return;
      }
    }
  }

  /* =====================
     Helpers
  ====================== */

  private updateMessageStatus(
    id: string,
    status: NonNullable<ChatMessage["status"]>,
  ) {
    this.setState({
      messages: this.state.messages.map((m) =>
        m.id === id
          ? { ...m, status } // ✅ always defined now
          : m,
      ),
    });
  }

  private flushStream(assistantId: string): void {
    const next = (this.state.streaming?.content ?? "") + this.streamBuffer;

    this.streamBuffer = "";

    this.setState({
      streaming: {
        active: true,
        messageId: assistantId,
        content: next,
      },
    });
  }

  /* =====================
     Send
  ====================== */

  async send(text: string, opts?: { retry?: boolean }): Promise<void> {
    const content = text.trim();
    if (!content || this.state.sending) return;

    let baseMessages = this.state.messages;
    let userMessageId = this.lastUserMessageId;

    if (!opts?.retry) {
      const userMsg: ChatMessage = {
        id: localId("u"),
        role: "USER",
        content,
        createdAt: now(),
        status: "sending", // ✅ FIXED
      };

      baseMessages = [...this.state.messages, userMsg];
      userMessageId = userMsg.id;

      this.lastUserMessage = content;
      this.lastUserMessageId = userMsg.id;
    }

    const assistantId = localId("a");

    this.setState({
      sending: true,
      messages: baseMessages,
      streaming: { active: true, messageId: assistantId, content: "" },
      error: undefined,
      showSuggestions: false,
      streamStatus: "connecting",
    });

    if (!opts?.retry) {
      this.clearInput();
      clearDraft(
        this.config.agentId,
        this.visitorId,
        this.state.conversationId,
      );
    }

    let conversationId = this.state.conversationId;

    if (!conversationId) {
      try {
        const created = await this.api.createConversation(
          this.config.agentId,
          this.visitorId,
          true,
        );

        conversationId = created.conversationId;
        setActiveConversationId(
          this.config.agentId,
          this.visitorId,
          conversationId,
        );
        this.setState({ conversationId });
      } catch {
        this.failMessage(userMessageId);
        return;
      }
    }

    const params = {
      agentId: this.config.agentId,
      visitorId: this.visitorId,
      conversationId,
      input: content,
    };

    const maxAttempts = 3;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      this.streamBuffer = "";

      if (this.streamFlushTimer) {
        clearTimeout(this.streamFlushTimer);
        this.streamFlushTimer = undefined;
      }

      if (!this.state.online) {
        this.setOfflineFailure(userMessageId);
        return;
      }

      if (attempt > 1) {
        this.setState({ streamStatus: "reconnecting" });
      }

      let gotAnyToken = false;
      let schedulingSlots: SchedulingSlot[] | undefined;
      let schedulingProvider: SchedulingProvider | undefined;
      const session = ++this.streamSession;

      let result: { ok: boolean } = { ok: false };

      try {
        result = await this.stream.start(params, (e) => {
          if (session !== this.streamSession) return;

          if (e.type === "token") {
            gotAnyToken = true;

            if (this.state.streamStatus !== "streaming") {
              this.setState({ streamStatus: "streaming" });
            }

            this.streamBuffer += e.value;

            if (!this.streamFlushTimer) {
              this.streamFlushTimer = window.setTimeout(() => {
                if (session !== this.streamSession) return;
                this.streamFlushTimer = undefined;
                this.flushStream(assistantId);
              }, 50);
            }
          } else if (e.type === "scheduling_slots") {
            schedulingSlots = Array.isArray(e.slots) ? e.slots : undefined;
            schedulingProvider = e.provider;
          } else if (e.type === "calendly_slots") {
            schedulingSlots = Array.isArray(e.slots) ? e.slots : undefined;
            schedulingProvider = "CALENDLY";
          } else if (e.type === "done") {
            this.streamSession++;

            if (this.streamFlushTimer) {
              clearTimeout(this.streamFlushTimer);
              this.streamFlushTimer = undefined;
            }

            const finalContent =
              (this.state.streaming?.content ?? "") + this.streamBuffer;

            this.streamBuffer = "";

            const assistantMsg: ChatMessage = {
              id: assistantId,
              role: "AGENT",
              content: finalContent,
              createdAt: now(),
              status: "sent",
              ...(schedulingSlots?.length
                ? {
                    schedulingSlots,
                    ...(schedulingProvider
                      ? { schedulingProvider }
                      : {}),
                    bookingStatus: "idle" as const,
                  }
                : {}),
            };

            if (userMessageId) {
              this.updateMessageStatus(userMessageId, "sent");
            }

            this.setState({
              sending: false,
              streaming: undefined,
              streamStatus: "idle",
              messages: [...baseMessages, assistantMsg],
            });

            if (this.config.showRecentChats) {
              this.api
                .listConversations(this.config.agentId, this.visitorId)
                .then((conversations) => this.setState({ conversations }))
                .catch(() => {});
            }
          } else if (e.type === "error") {
            console.error("Stream error:", e.message);
            this.failMessage(userMessageId);
          }
        });
      } catch {
        result = { ok: false };
      }

      if (result.ok) return;

      if (!gotAnyToken && attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 400 * attempt));
        continue;
      }

      this.failMessage(userMessageId);
      return;
    }
  }

  /* =====================
     Failure helpers
  ====================== */

  private failMessage(userMessageId?: string) {
    this.streamSession++;

    if (this.streamFlushTimer) {
      clearTimeout(this.streamFlushTimer);
      this.streamFlushTimer = undefined;
    }

    if (userMessageId) {
      this.updateMessageStatus(userMessageId, "error");
    }

    this.setState({
      sending: false,
      streaming: undefined,
      streamStatus: "idle",
      error: this.makeError("stream"),
    });
  }

  private setOfflineFailure(userMessageId?: string) {
    if (userMessageId) {
      this.updateMessageStatus(userMessageId, "error");
    }

    this.setState({
      sending: false,
      streaming: undefined,
      streamStatus: "idle",
      error: this.makeError("offline"),
    });
  }

  async bookSchedulingSlot(
    messageId: string,
    slot: SchedulingSlot,
    attendee: { name: string; email: string },
  ): Promise<void> {
    const conversationId = this.state.conversationId;
    if (!conversationId) throw new Error("No active conversation.");

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

    const setBookingState = (
      status: "booking" | "confirmed" | "error",
      bookingError?: string,
    ) => {
      this.setState({
        messages: this.state.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                bookingStatus: status,
                ...(bookingError ? { bookingError } : {}),
              }
            : m,
        ),
      });
    };

    setBookingState("booking");
    try {
      const response = await this.api.confirmSchedulingBooking(conversationId, {
        start_time: slot.start_time,
        name: attendee.name.trim(),
        email: attendee.email.trim(),
        timezone,
      });

      const confirmation: ChatMessage = {
        id: localId("a"),
        role: "AGENT",
        content: response.message || "Your meeting is confirmed.",
        createdAt: now(),
        status: "sent",
      };

      this.setState({
        messages: [
          ...this.state.messages.map((m) =>
            m.id === messageId
              ? (() => {
                  const { bookingError: _omit, ...rest } = m;
                  return { ...rest, bookingStatus: "confirmed" as const };
                })()
              : m,
          ),
          confirmation,
        ],
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Unable to confirm booking.";
      setBookingState("error", message);
      throw err;
    }
  }

  /* =====================
     Branding
  ====================== */

  private applyBranding(agent: import("./state").AgentPublic): void {
    const host = this.hostElement;
    if (!host) return;

    host.style.setProperty("--cg-primary", agent.primaryColor);
    host.style.setProperty("--cg-on-primary", agent.textColor);
    host.style.setProperty("--cg-bubble-user", agent.chatBubbleColor);
  }

  private makeError(kind: WidgetError["kind"]): WidgetError {
    switch (kind) {
      case "init":
        return {
          kind: "init",
          title: "Unable to start chat",
          description:
            "We couldn’t load the chat right now. Please check your connection and try again.",
          actionLabel: "Retry",
          recoverable: true,
        };

      case "conversation":
        return {
          kind: "conversation",
          title: "Couldn’t open this conversation",
          description:
            "This chat could not be loaded. It may have expired or been removed.",
          actionLabel: "Try again",
          recoverable: true,
        };

      case "offline":
        return {
          kind: "offline",
          title: "You’re offline",
          description:
            "Your internet connection is unavailable. Reconnect to continue chatting.",
          actionLabel: "Retry",
          recoverable: true,
        };

      case "stream":
        return {
          kind: "stream",
          title: "Message failed to send",
          description:
            "We couldn’t deliver your message. Please try sending it again.",
          actionLabel: "Retry",
          recoverable: true,
        };
    }
  }

  private validateWebsiteAccess(agent: import("./state").AgentPublic): boolean {
    // 1️⃣ Widget disabled by owner
    if (!agent.websiteIntegrationEnabled) {
      console.error(
        `[ChatGrow] Widget is disabled for agent "${agent.name}". Enable it in the dashboard.`,
      );
      return false;
    }

    // 2️⃣ Subscription inactive
    const subscriptionStatus = agent.user?.subscription?.status;

    if (subscriptionStatus === "CANCELED") {
      // console.error(
      //   `[ChatGrow] Widget blocked: subscription is "${subscriptionStatus ?? 'NONE'}" for agent "${agent.name}".`
      // );
      return false;
    }

    // 3️⃣ Domain restriction
    const allowed = agent.allowedDomains ?? [];

    // If no domains configured → allow everywhere
    if (!allowed.length) return true;

    const host = getWidgetHost().toLowerCase();

    const allowedMatch = allowed.some((domain) => {
      const d = domain.toLowerCase();

      // exact match
      if (d === host) return true;

      // wildcard match (*.example.com)
      if (d.startsWith("*.")) {
        return host.endsWith(d.slice(2));
      }

      return false;
    });

    if (!allowedMatch) {
      console.error(
        `[ChatGrow] Domain "${host}" is not allowed for this widget.`,
        { allowedDomains: allowed },
      );
      return false;
    }

    return true;
  }
}
